import { useEffect, useMemo, useRef, useState } from "react";
import PageLayout from "./PageLayout";
import { Account } from "./TopMenuBar";
import { AchievementDef, useAchievements } from "./AchievementsProvider";
import { useViewport } from "../hooks/useViewport";
import OverlayPortal from "./OverlayPortal";
import { ANDROID_TABLET_MENU_OFFSET, ANDROID_TABLET_SCROLL_REDUCTION } from "../lib/androidLayout";

interface AchievementsPageProps {
  account: Account;
  onLogout?: () => void;
}

type TabKey = "new" | "earned" | "all";
const LEADERBOARD_BG = "LeaderboardBackground.png";

function getAssetPath(path: string): string {
  const cleanPath = path.replace(/^\//, "");
  const isBrowser = typeof window !== "undefined";

  const cap = isBrowser ? (window as any).Capacitor : null;
  const isNative =
    Boolean(cap) &&
    (typeof cap.isNativePlatform === "function"
      ? cap.isNativePlatform()
      : typeof cap.getPlatform === "function"
        ? cap.getPlatform() === "ios" || cap.getPlatform() === "android"
        : cap.platform === "ios" || cap.platform === "android");

  if (isNative) {
    try {
      const baseUrl = new URL(window.location.href);
      return baseUrl.origin + "/" + cleanPath;
    } catch (e) {
      console.warn("[getAssetPath] Failed to construct URL, using fallback:", e);
      return "/" + cleanPath;
    }
  }

  return "./" + cleanPath;
}

function buildInitials(name: string): string {
  const parts = name.split(" ").filter(Boolean);
  if (parts.length === 0) return "A";
  return parts.map((part) => part[0].toUpperCase()).join("").slice(0, 2);
}

function AchievementIcon({
  name,
  iconKey,
  frameSize,
  onClick,
}: {
  name: string;
  iconKey: string;
  frameSize: number;
  onClick?: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const src = iconKey ? getAssetPath(`achievement-icons/${iconKey}`) : "";
  const hasImage = Boolean(!failed && src);

  useEffect(() => {
    setFailed(false);
  }, [iconKey]);

  return (
    <button
      type="button"
      className="flex items-center justify-center transition-transform hover:scale-105"
      style={{
        width: `${frameSize}px`,
        height: `${frameSize}px`,
        background: hasImage ? "transparent" : "rgba(245, 214, 164, 0.9)",
        fontWeight: 700,
        color: "#3e2615",
        overflow: "hidden",
        borderRadius: "10px",
        border: "none",
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={onClick}
      disabled={!onClick}
      aria-label={`Preview ${name}`}
    >
      {hasImage ? (
        <img
          src={src}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={() => setFailed(true)}
        />
      ) : (
        buildInitials(name)
      )}
    </button>
  );
}

export default function AchievementsPage({ account, onLogout }: AchievementsPageProps) {
  const { achievements, earned, refresh, markViewed } = useAchievements();
  const [activeTab, setActiveTab] = useState<TabKey>("earned");
  const [loaded, setLoaded] = useState(false);
  const [previewAchievement, setPreviewAchievement] = useState<AchievementDef | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const seenNewIdsRef = useRef<Set<string>>(new Set());
  const pageRows = 6;
  const [visibleRows, setVisibleRows] = useState(pageRows);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(false);
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const columnCount = viewport.width >= 768 ? 2 : 1;
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const isAndroid = typeof window !== "undefined" && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === "android";
  const isIOSApp = typeof window !== "undefined" && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === "ios";
  
  // Bulletproof iPhone detection - exclude iPad (must be after viewport declaration)
  const isIPhone = (() => {
    if (!isIOSApp) return false;
    const userAgent = typeof navigator !== 'undefined' ? (navigator.userAgent || navigator.vendor || '').toLowerCase() : '';
    const platform = typeof navigator !== 'undefined' ? (navigator as any).platform || '' : '';
    const maxTouchPoints = typeof navigator !== 'undefined' ? (navigator as any).maxTouchPoints || 0 : 0;
    const maxDim = Math.max(viewport.width, viewport.height);
    const minDim = Math.min(viewport.width, viewport.height);
    
    // Explicit iPad in user agent
    if (userAgent.includes('ipad')) return false;
    
    // Modern iPadOS 13+ reports as MacIntel with touch support
    if (platform.toLowerCase() === 'macintel' && maxTouchPoints > 1 && maxDim >= 768 && minDim >= 768) return false;
    
    // If dimensions suggest iPad, exclude it
    if (maxDim >= 768 && minDim >= 768) return false;
    
    // Explicit iPhone in user agent (but not iPad)
    if (userAgent.includes('iphone') && !userAgent.includes('ipad')) return true;
    
    // iPod touch counts as iPhone-like
    if (userAgent.includes('ipod')) return true;
    
    // If it's iOS app and not iPad (by dimensions), assume iPhone
    if (isIOSApp && (maxDim < 768 || minDim < 768)) return true;
    
    return false;
  })();
  
  // Bulletproof iPad detection - exclude iPhone (must be after isIPhone declaration)
  const isIOSPad = (() => {
    if (!isIOSApp) return false;
    if (isIPhone) return false; // If it's iPhone, it's not iPad
    return Math.max(viewport.width, viewport.height) >= 768;
  })();
  const isAndroidTablet = viewport.isAndroidTablet;
  const androidInsetLeft = isAndroid ? Math.round(viewport.width * (70 / 1920)) : 0;
  const androidInsetRight = isAndroid ? Math.round(viewport.width * (112 / 1920)) : 0;
  const androidMenuOffset = isAndroidTablet ? ANDROID_TABLET_MENU_OFFSET : 0;
  const scrollReduction = isAndroidTablet ? ANDROID_TABLET_SCROLL_REDUCTION : 0;
  const textScale = isAndroidTablet ? 3.6 : (isIOSPad ? 1.18 : 1);
  const androidContentShift = isAndroidTablet ? 25 : 0;
  const androidBodyFontSize = isAndroidTablet ? "18px" : undefined;
  const androidBodyLineHeight = isAndroidTablet ? 1.3 : undefined;
  const iosPadGutterX = isIOSPad ? Math.round(viewport.width * 0.05) : 0; // ~10% width inset on iPad
  const iosPadGutterY = isIOSPad ? Math.round(viewport.height * 0.05) : 0; // shrink height ~10% on iPad
  const iosPadShiftX = isIOSPad ? 10 : 0; // slight right nudge
  const iosPadShiftY = isIOSPad ? 24 : 0; // doubled downward nudge
  const androidTextStyle = isAndroidTablet ? (
    <style>{`
      .android-tablet-text-achievements, .android-tablet-text-achievements * {
        font-size: ${androidBodyFontSize};
        line-height: ${androidBodyLineHeight};
      }
    `}</style>
  ) : null;
  const iosTopPad = isIOSPad ? 72 : 0;
  const iosBottomPad = isIOSPad ? 110 : 0;
  const backgroundSrc = (isIOSPad || isIPhone) ? "TabletBackground.png" : LEADERBOARD_BG;
  // iPhone-specific adjustments: move right and make narrower (matching Leaderboard)
  const iphoneRightShift = isIPhone ? 10 : 0; // Move right by 10px (keeps left side in place)
  const iphoneRightNarrow = isIPhone ? 27 : 0; // Add 27px to right to make narrower
  const isCompact = viewport.isMobile && !isMobileLandscape;
  const iconFrameSize = isCompact ? 64 : 84;
  const previewCardWidthPx = Math.min(
    viewport.width * (isCompact ? 0.94 : 0.9),
    isCompact ? 360 : 420
  );
  const previewMaxHeightPx = Math.max(
    240,
    viewport.height - (isMobileLandscape ? 24 : 48)
  );
  const previewChromeHeight = isMobileLandscape ? 128 : 170;
  const previewImageSizePx = Math.max(
    96,
    Math.min(previewCardWidthPx - 40, previewMaxHeightPx - previewChromeHeight)
  );
  const previewCardWidth = `${Math.round(previewCardWidthPx)}px`;
  const previewImageSize = `${Math.round(previewImageSizePx)}px`;
  const previewMaxHeight = `${Math.round(previewMaxHeightPx)}px`;
  const visibleCount = visibleRows * columnCount;

  useEffect(() => {
    let cancelled = false;
    refresh().finally(() => {
      if (!cancelled) {
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const earnedMap = useMemo(() => {
    const map = new Map<string, { earned_at: string; viewed_at: string | null }>();
    for (const item of earned) {
      map.set(item.achievement_id, { earned_at: item.earned_at, viewed_at: item.viewed_at });
    }
    return map;
  }, [earned]);

  const sortedAchievements = useMemo(() => {
    return [...achievements].sort((a, b) => {
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.name.localeCompare(b.name);
    });
  }, [achievements]);

  const newAchievements = useMemo(
    () => sortedAchievements.filter((achievement) => {
      const earnedRow = earnedMap.get(achievement.id);
      return earnedRow && !earnedRow.viewed_at;
    }),
    [earnedMap, sortedAchievements]
  );

  const earnedAchievements = useMemo(
    () => sortedAchievements.filter((achievement) => {
      const earnedRow = earnedMap.get(achievement.id);
      return Boolean(earnedRow);
    }),
    [earnedMap, sortedAchievements]
  );

  const allAchievements = sortedAchievements;

  // Set initial tab to "new" only if there are new achievements, otherwise stay on "earned"
  const initialTabSetRef = useRef(false);
  useEffect(() => {
    if (!loaded) return;
    if (initialTabSetRef.current) return; // Only set once when data loads
    
    if (newAchievements.length > 0) {
      setActiveTab("new");
    }
    initialTabSetRef.current = true;
  }, [loaded, newAchievements.length]);

  useEffect(() => {
    if (!loaded) return;
    if (activeTab !== "new") return;
    if (newAchievements.length === 0) return;

    const target = seenNewIdsRef.current;
    for (const achievement of newAchievements) {
      target.add(achievement.id);
    }
  }, [activeTab, loaded, newAchievements]);

  useEffect(() => {
    return () => {
      const ids = Array.from(seenNewIdsRef.current);
      if (ids.length === 0) return;
      void markViewed({ achievementIds: ids });
    };
  }, [markViewed]);

  const tabContent = useMemo(() => {
    if (activeTab === "new") return newAchievements;
    if (activeTab === "earned") return earnedAchievements;
    return allAchievements;
  }, [activeTab, allAchievements, earnedAchievements, newAchievements]);

  const emptyMessage = useMemo(() => {
    if (activeTab === "new") return "No new achievements yet.";
    if (activeTab === "earned") return "No earned achievements yet.";
    return "No achievements to show yet.";
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "all") {
      setVisibleRows(pageRows);
    }
  }, [activeTab, pageRows]);

  useEffect(() => {
    setPreviewFailed(false);
  }, [previewAchievement?.icon_key]);

  const updateScrollIndicators = (el: HTMLDivElement | null) => {
    if (!el) return;
    const isAtTop = el.scrollTop === 0;
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 1;
    setShowTopIndicator(!isAtTop);
    setShowBottomIndicator(!isAtBottom);
  };

  useEffect(() => {
    if (isIOSPad && scrollRef.current) {
      requestAnimationFrame(() => updateScrollIndicators(scrollRef.current));
    }
  }, [isIOSPad, tabContent, activeTab, isMobileLandscape]);

  return (
    <PageLayout account={account} onLogout={onLogout}>
      {(isAndroid || isIOSPad || isIPhone) && (
        <div
          className="fixed inset-0 w-screen h-screen overflow-hidden"
          style={{ zIndex: 0, pointerEvents: "none" }}
          aria-hidden="true"
        >
          <img
            src={getAssetPath(backgroundSrc)}
            alt=""
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: "cover", objectPosition: "center" }}
            draggable={false}
          />
        </div>
      )}
      {androidTextStyle}
      <div
        className={`${isMobileLandscape ? "fixed z-40 p-2 flex flex-col" : "px-2 md:px-6 pb-6"} ${isAndroidTablet ? "android-tablet-text-achievements" : ""}`}
        style={isMobileLandscape ? (
          isIOSPad ? {
            top: isAndroidTablet
              ? `calc(32px + ${androidMenuOffset}px)`
              : (isAndroid ? "32px" : `calc(env(safe-area-inset-top, 0px) + ${iosTopPad}px + ${iosPadGutterY / 2 + iosPadShiftY}px)`),
            left: isAndroid
              ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${androidInsetLeft}px)`
              : `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${iosPadGutterX}px)`,
            right: isAndroid
              ? `calc(env(safe-area-inset-right, 0px) + 48px + ${androidInsetRight}px)`
              : `calc(env(safe-area-inset-right, 0px) + 48px + ${iosPadGutterX}px)`,
            bottom: scrollReduction
              ? `calc(env(safe-area-inset-bottom, 8px) - 14px + 20px + ${scrollReduction}px + ${iosBottomPad}px)`
              : `calc(env(safe-area-inset-bottom, 8px) - 14px + 20px + ${iosBottomPad}px + ${iosPadGutterY / 2}px)`,
            display: "flex",
            flexDirection: "column",
            marginLeft: "-20px",
            marginRight: 0,
            fontSize: textScale > 1 ? `${textScale}em` : undefined,
            lineHeight: textScale > 1 ? 1.2 : undefined,
            transform: (androidContentShift || iosPadShiftX) ? `translateX(${(androidContentShift || 0) + iosPadShiftX}px)` : undefined,
            ...(androidBodyFontSize ? { fontSize: androidBodyFontSize, lineHeight: androidBodyLineHeight } : {}),
            overflow: "hidden",
          } : {
            top: isAndroidTablet
              ? `calc(32px + ${androidMenuOffset}px)`
              : (isAndroid ? "32px" : (isIPhone ? "32px" : "env(safe-area-inset-top, 0px)")),
            left: isAndroid
              ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${androidInsetLeft}px)`
              : isIPhone
              ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${iphoneRightShift}px)`
              : "calc(env(safe-area-inset-left, 0px) + 32px + 112px)",
            right: isAndroid
              ? `calc(env(safe-area-inset-right, 0px) + 48px + ${androidInsetRight}px)`
              : isIPhone
              ? `calc(env(safe-area-inset-right, 0px) + 48px + ${iphoneRightNarrow}px)`
              : "calc(env(safe-area-inset-right, 0px) + 48px)",
            bottom: scrollReduction
              ? `calc(env(safe-area-inset-bottom, 8px) - 14px + 60px + ${scrollReduction}px)`
              : "calc(env(safe-area-inset-bottom, 8px) - 14px + 60px)",
            overflowY: "auto",
            overflowX: "auto",
            display: "flex",
            flexDirection: "column",
            marginLeft: "-20px",
            marginRight: 0,
            fontSize: textScale > 1 ? `${textScale}em` : undefined,
            lineHeight: textScale > 1 ? 1.2 : undefined,
            transform: androidContentShift ? `translateX(${androidContentShift}px)` : undefined,
            ...(androidBodyFontSize ? { fontSize: androidBodyFontSize, lineHeight: androidBodyLineHeight } : {}),
          }
        ) : (isAndroid ? { paddingLeft: `${androidInsetLeft}px`, paddingRight: `${androidInsetRight}px`, fontSize: textScale > 1 ? `${textScale}em` : undefined, lineHeight: textScale > 1 ? 1.2 : undefined } : (textScale > 1 ? { fontSize: `${textScale}em`, lineHeight: 1.2 } : {}))}
      >
        <div
          className={`rounded-2xl border-4 shadow-xl ${isMobileLandscape && isIOSPad ? "p-3 flex-1 flex flex-col" : isMobileLandscape ? "p-3" : "px-4 py-4 md:px-6 md:py-6"}`}
          style={isMobileLandscape && isIOSPad ? {
            background: "#fffaeb",
            borderColor: "#8b6542",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
          } : {
            background: "#fffaeb",
            borderColor: "#8b6542",
          }}
        >
          <div className={isMobileLandscape && isIOSPad ? "relative flex-1 min-h-0" : ""} style={isMobileLandscape && isIOSPad ? { overflow: 'visible' } : {}}>
            <div
              ref={isIOSPad ? scrollRef : undefined}
              onScroll={isIOSPad ? (e) => updateScrollIndicators(e.currentTarget as HTMLDivElement) : undefined}
              className={isMobileLandscape && isIOSPad ? "overflow-y-auto flex-1" : ""}
              style={isMobileLandscape && isIOSPad ? { minHeight: 0, height: '100%' } : {}}
            >
            <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <div className={`${isIOSPad ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"} font-bold`} style={{ color: "#3e2615" }}>
                Achievements
              </div>
              <div className={`${isIOSPad ? "text-sm md:text-base" : "text-xs md:text-sm"}`} style={{ color: "#5a3b22" }}>
                Track your milestones and match feats.
              </div>
            </div>
            <div className="text-xs md:text-sm" style={{ color: "#5a3b22" }}>
              {account.username}
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            {(["new", "earned", "all"] as TabKey[]).map((tab) => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  className="px-3 py-1.5 rounded-full border-2 text-xs md:text-sm font-semibold transition-all"
                  style={{
                    background: isActive ? "#f7b267" : "#fffaeb",
                    borderColor: "#8b6542",
                    color: "#3e2615",
                    boxShadow: isActive ? "0 2px 0 #8b6542" : undefined,
                  }}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === "new" ? "New" : tab === "earned" ? "Earned" : "All"}
                </button>
              );
            })}
          </div>

          {tabContent.length === 0 ? (
            <div
              className="rounded-xl border-2 px-4 py-6 text-center text-sm"
              style={{ borderColor: "#8b6542", color: "#5a3b22", background: "rgba(245, 214, 164, 0.4)" }}
            >
              {emptyMessage}
            </div>
          ) : (
            <div 
              className="grid gap-3 md:gap-4 md:grid-cols-2"
              style={{ paddingBottom: '0.5rem' }}
            >
              {(activeTab === "all" ? tabContent.slice(0, visibleCount) : tabContent).map((achievement) => {
                const earnedRow = earnedMap.get(achievement.id);
                const isNew = Boolean(earnedRow && !earnedRow.viewed_at);
                const isEarned = Boolean(earnedRow);
                const isLocked = activeTab === "all" && !isEarned;
                return (
                  <div
                    key={achievement.id}
                    className="relative flex items-start gap-3 rounded-xl border-2 px-3 py-3 md:px-4 md:py-4"
                    style={{
                      borderColor: "#8b6542",
                      background: isEarned ? "rgba(255, 250, 235, 0.95)" : "rgba(245, 214, 164, 0.4)",
                    }}
                  >
                    <div
                      className="w-full"
                      style={
                        isLocked
                          ? {
                              filter: "blur(8px) grayscale(0.4)",
                              opacity: 0.6,
                              pointerEvents: "none",
                              userSelect: "none",
                            }
                          : undefined
                      }
                    >
                      <div className="flex items-start gap-3">
                        <AchievementIcon
                          name={achievement.name}
                          iconKey={achievement.icon_key}
                          frameSize={iconFrameSize}
                          onClick={isLocked ? undefined : () => setPreviewAchievement(achievement)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-sm md:text-base font-semibold" style={{ color: "#3e2615" }}>
                              {achievement.name}
                            </div>
                            {isNew && (
                              <span
                                className="text-[10px] font-bold px-2 py-0.5 rounded-full border"
                                style={{
                                  background: "#fee067",
                                  borderColor: "#8b6542",
                                  color: "#3e2615",
                                }}
                              >
                                NEW
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 text-xs md:text-sm" style={{ color: "#5a3b22" }}>
                        {achievement.description}
                      </div>
                    </div>
                    {isLocked && (
                      <div
                        className="absolute inset-0 flex items-center justify-center text-xs md:text-sm font-semibold uppercase tracking-wide"
                        style={{
                          color: "#7a5a2f",
                          background: "rgba(255, 245, 225, 0.4)",
                          pointerEvents: "none",
                        }}
                      >
                        Hidden Achievement
                      </div>
                    )}
                  </div>
                );
              })}
              {activeTab === "all" && tabContent.length > visibleCount && (
                <button
                  type="button"
                  className="mx-auto mt-2 rounded-full border-2 px-4 py-2 text-xs md:text-sm font-semibold transition-all hover:scale-105 md:col-span-2"
                  style={{
                    background: "#fffaeb",
                    borderColor: "#8b6542",
                    color: "#3e2615",
                  }}
                  onClick={() => setVisibleRows((prev) => prev + pageRows)}
                >
                  Load more
                </button>
              )}
            </div>
          )}
            </div>
          {isIOSPad && isMobileLandscape && (
            <>
              {showTopIndicator && (
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 flex justify-center" style={{ top: '-20px', zIndex: 45 }}>
                  <svg width={28} height={18} viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.65))' }}>
                    <path d="M1 7L6 2L11 7" stroke="#f7b267" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
              {showBottomIndicator && (
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 flex justify-center" style={{ bottom: '-20px', zIndex: 45 }}>
                  <svg width={28} height={18} viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.65))' }}>
                    <path d="M1 1L6 6L11 1" stroke="#f7b267" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </>
          )}
          </div>
        </div>
      </div>
      {previewAchievement && (
        <OverlayPortal>
          <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ zIndex: 200 }}
          >
            <div
              className="absolute inset-0"
              style={{ background: "rgba(0, 0, 0, 0.45)" }}
              onClick={() => setPreviewAchievement(null)}
            />
              <div
                className={`relative rounded-2xl border-4 shadow-xl text-center ${isMobileLandscape ? "px-3 py-3" : "px-4 py-5"}`}
                style={{
                  background: "#fffaeb",
                  borderColor: "#8b6542",
                  width: previewCardWidth,
                  maxHeight: previewMaxHeight,
                  overflowY: isMobileLandscape ? "hidden" : "auto",
                }}
              >
              <div
                className={`${isMobileLandscape ? "text-sm mb-2" : "text-base mb-3"} font-semibold`}
                style={{ color: "#3e2615" }}
              >
                {previewAchievement.name}
              </div>
              <div
                className="mx-auto flex items-center justify-center rounded-2xl border-2"
                style={{
                  width: previewImageSize,
                  height: previewImageSize,
                  background: "rgba(245, 214, 164, 0.4)",
                  borderColor: "#8b6542",
                }}
              >
                {!previewFailed ? (
                  <img
                    src={getAssetPath(`achievement-icons/${previewAchievement.icon_key}`)}
                    alt=""
                    style={{ width: "80%", height: "80%", objectFit: "contain" }}
                    onError={() => setPreviewFailed(true)}
                  />
                ) : (
                  <div className="text-lg font-bold" style={{ color: "#3e2615" }}>
                    {buildInitials(previewAchievement.name)}
                  </div>
                )}
              </div>
              <div
                className={`${isMobileLandscape ? "mt-2 text-xs" : "mt-3 text-xs"} italic`}
                style={{ color: "#5a3b22" }}
              >
                {previewAchievement.description}
              </div>
              <button
                type="button"
                className={`${isMobileLandscape ? "mt-3 py-1.5 text-xs" : "mt-4 py-2 text-sm"} w-full rounded-lg border-2 font-semibold transition-all hover:scale-105`}
                style={{
                  background: "#fffaeb",
                  borderColor: "#8b6542",
                  color: "#3e2615",
                }}
                onClick={() => setPreviewAchievement(null)}
              >
                Close
              </button>
            </div>
          </div>
        </OverlayPortal>
      )}
    </PageLayout>
  );
}
